import { useCallback, useEffect, useState } from 'react';
import { adminRoleOf, gate, stillAdmin, STILL_ADMIN_CHECK_MS, type Gate } from './auth/mfa';
import { DEFAULT_LIMITS, sessionState, type SessionTimes } from './auth/session';
import { Console } from './Console';
import { supabase } from './lib/supabase';
import { CodePage, EnrolPage, NotAdminPage, SignInPage } from './pages/SignIn';

export function App() {
  const [g, setG] = useState<Gate | 'loading'>('loading');
  const [times, setTimes] = useState<SessionTimes | null>(null);
  const [role, setRole] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!supabase) return setG('signed_out');
    const next = await gate(supabase);
    setG(next);
    const { data: s } = await supabase.auth.getSession();
    setRole(adminRoleOf(s.session?.access_token));
    if (next === 'ok' && !times) {
      const now = Date.now();
      setTimes({ startedAt: now, lastActivityAt: now, lastSecondFactorAt: now });
    }
  }, [times]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // ADM-003: watch for the idle and the 12-hour limits.
  useEffect(() => {
    if (!times) return;
    const touch = () => setTimes((t) => (t ? { ...t, lastActivityAt: Date.now() } : t));
    const tick = setInterval(() => {
      const s = sessionState(times, Date.now(), DEFAULT_LIMITS);
      if (s.kind === 'ended') {
        void supabase?.auth.signOut();
        setTimes(null);
        setG('signed_out');
      }
    }, 15_000);
    // ADM-006: a removed admin is signed out within 60 seconds.
    const check = setInterval(() => {
      if (!supabase) return;
      void stillAdmin(supabase).then((yes) => {
        if (yes === false) {
          void supabase?.auth.signOut();
          setTimes(null);
          setG('signed_out');
        }
      });
    }, STILL_ADMIN_CHECK_MS);
    window.addEventListener('pointerdown', touch);
    window.addEventListener('keydown', touch);
    return () => {
      clearInterval(tick);
      clearInterval(check);
      window.removeEventListener('pointerdown', touch);
      window.removeEventListener('keydown', touch);
    };
  }, [times]);

  if (!supabase)
    return (
      <main>
        <h1>Fairway Life admin</h1>
        <p>
          The database is not connected yet. This page works once the staging database is set up.
        </p>
      </main>
    );
  if (g === 'loading') return <main>Loading…</main>;
  if (g === 'signed_out') return <SignInPage sb={supabase} onDone={refresh} />;
  if (g === 'needs_enrol') return <EnrolPage sb={supabase} onDone={refresh} />;
  if (g === 'needs_code') return <CodePage sb={supabase} onDone={refresh} />;
  if (g === 'not_admin') return <NotAdminPage sb={supabase} onDone={refresh} />;

  return (
    <Console
      sb={supabase}
      role={role}
      onSignOut={() => void supabase!.auth.signOut().then(refresh)}
    />
  );
}
